import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { applySEO, SITE_URL, breadcrumbSchema } from "@/lib/seo";

export default function Lookbook() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ collection: "", season: "", sort: "newest", search: "" });

  useEffect(() => {
    applySEO({
      title: "Lookbook — Asymmetry Edits",
      description: "Curated looks crafted to inspire your personal style. Editorial fashion moments from the Asymmetry Edits atelier.",
      canonical: `${SITE_URL}/lookbook`,
      jsonLd: [{ id: "ld-lookbook-crumb", data: breadcrumbSchema([{name:"Home",url:"/"},{name:"Lookbook",url:"/lookbook"}])}],
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.collection) params.collection = filters.collection;
    if (filters.season) params.season = filters.season;
    if (filters.sort) params.sort = filters.sort;
    if (filters.search) params.search = filters.search;
    api.get("/looks", { params }).then((r) => setLooks(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const collections = [...new Set(looks.map(l => l.collection_name).filter(Boolean))];
  const seasons = [...new Set(looks.map(l => l.season).filter(Boolean))];

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main data-testid="lookbook-page" className="pt-28 md:pt-32">
        <section className="relative py-24 md:py-36 px-6 md:px-12 lg:px-24 border-b border-white/8">
          <div className="eyebrow mb-4">The House</div>
          <h1 className="font-serif text-white text-5xl md:text-7xl leading-[0.95]">Lookbook.</h1>
          <p className="mt-6 max-w-xl text-white/60 font-light text-base md:text-lg">Curated looks crafted to inspire your personal style.</p>
        </section>

        <section className="px-6 md:px-12 lg:px-24 py-10 border-b border-white/8 flex flex-wrap gap-3 items-center">
          <input type="search" placeholder="Search looks…" value={filters.search} onChange={(e)=>setFilters({...filters,search:e.target.value})} className="lux-input flex-1 min-w-[220px] max-w-sm" data-testid="lookbook-search" />
          {collections.length>0 && <select value={filters.collection} onChange={(e)=>setFilters({...filters,collection:e.target.value})} className="bg-midnight-deep border border-white/15 text-white px-3 py-2 text-xs tracking-[0.2em] uppercase"><option value="">All Collections</option>{collections.map(c=><option key={c} value={c}>{c}</option>)}</select>}
          {seasons.length>0 && <select value={filters.season} onChange={(e)=>setFilters({...filters,season:e.target.value})} className="bg-midnight-deep border border-white/15 text-white px-3 py-2 text-xs tracking-[0.2em] uppercase"><option value="">All Seasons</option>{seasons.map(s=><option key={s} value={s}>{s}</option>)}</select>}
          <select value={filters.sort} onChange={(e)=>setFilters({...filters,sort:e.target.value})} className="bg-midnight-deep border border-white/15 text-white px-3 py-2 text-xs tracking-[0.2em] uppercase"><option value="newest">Newest</option><option value="oldest">Oldest</option></select>
        </section>

        <section className="px-6 md:px-12 lg:px-24 py-12 md:py-20">
          {loading ? <div className="text-white/50">Loading…</div> : looks.length===0 ? (
            <div className="text-center py-20 text-white/55"><h3 className="font-serif text-3xl mb-2 text-white">No looks yet.</h3><p>New editorials arrive soon.</p></div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
              {looks.map((l,i) => (
                <Link key={l.id} to={`/lookbook/${l.slug}`} data-testid={`look-card-${l.id}`} className="group block break-inside-avoid overflow-hidden bg-midnight-panel">
                  <div className="relative overflow-hidden" style={{aspectRatio: i%3===0?"3/4":i%3===1?"4/5":"1/1"}}>
                    <img src={l.cover_image} alt={l.alt||l.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b10] via-transparent to-transparent opacity-70" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-silver/80">{l.collection_name}{l.season?` · ${l.season}`:""}</div>
                      <div className="font-serif text-white text-2xl md:text-3xl leading-tight mt-2">{l.title}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
